import { appBackendApiClient } from './apiClient';

export type DonationTrackingStatus =
  | 'RECEIVED'
  | 'PROCESSING'
  | 'DISPATCHING'
  | 'DELIVERED';

export type BeneficiaryCountry = 'EGYPT' | 'GAZA';

export interface DonationTrackingEvent {
  id: string;
  status: DonationTrackingStatus;
  message: string;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
}

export interface DonationTrackingDonation {
  id: string;
  amount: string;
  currency: string;
  status: string;
  createdAt: string;
}

export interface DonationTrackingCause {
  id: string;
  title: string;
  coverImageUrl: string | null;
}

export interface DonationTracking {
  id: string;
  donationId: string;
  userId: string;
  causeId: string | null;
  status: DonationTrackingStatus;
  estimatedDeliveryDate: string | null;
  beneficiaryCountry: BeneficiaryCountry;
  beneficiaryRegion: string;
  latitude: number;
  longitude: number;
  beneficiaryLabel: string;
  beneficiaryCount: number;
  isSimulated: boolean;
  deliveredAt: string | null;
  createdAt: string;
  updatedAt: string;
  events?: DonationTrackingEvent[];
  donation?: DonationTrackingDonation;
  cause?: DonationTrackingCause | null;
}

export interface PaginatedTrackings {
  items: DonationTracking[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface DonationTrackingRoutingLog {
  id: string;
  trackingId: string;
  donationId: string;
  causeId: string | null;
  assignedCountry: BeneficiaryCountry;
  assignedRegion: string;
  latitude: string;
  longitude: string;
  beneficiaryCount: number;
  beneficiaryLabel: string;
  routingReason: string;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
}

export interface PaginatedRoutingLogs {
  items: DonationTrackingRoutingLog[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface CauseTrackingConfigRegion {
  region: string;
  latitude: number;
  longitude: number;
}

export interface CauseTrackingConfig {
  id: string;
  causeId: string;
  beneficiaryCountry: BeneficiaryCountry;
  mealPrice: string;
  estimatedDeliveryDaysMin: number;
  estimatedDeliveryDaysMax: number;
  receivedToProcessingDelay: number;
  processingToDispatchingDelay: number;
  dispatchingToDeliveredDelay: number;
  allowedRegions: CauseTrackingConfigRegion[];
  notificationTemplates: Record<string, string> | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertCauseTrackingConfigPayload {
  beneficiaryCountry?: BeneficiaryCountry;
  mealPrice?: number;
  estimatedDeliveryDaysMin?: number;
  estimatedDeliveryDaysMax?: number;
  receivedToProcessingDelay?: number;
  processingToDispatchingDelay?: number;
  dispatchingToDeliveredDelay?: number;
  allowedRegions?: CauseTrackingConfigRegion[];
  notificationTemplates?: Record<string, string> | null;
  isActive?: boolean;
}

export interface ListTrackingFilter {
  status?: DonationTrackingStatus;
  country?: BeneficiaryCountry;
  causeId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export const DonationTrackingService = {
  list: async (filter: ListTrackingFilter = {}): Promise<PaginatedTrackings> => {
    const response = await appBackendApiClient.get('/admin/donation-tracking', {
      params: filter,
    });
    return response.data;
  },

  backfill: async (): Promise<{ created: number; skipped: number; failed: number }> => {
    const response = await appBackendApiClient.post(
      '/admin/donation-tracking/backfill',
    );
    return response.data;
  },

  getById: async (id: string): Promise<DonationTracking> => {
    const response = await appBackendApiClient.get(
      `/admin/donation-tracking/${id}`,
    );
    return response.data;
  },

  updateStatus: async (
    id: string,
    status: DonationTrackingStatus,
    message?: string,
  ): Promise<DonationTracking> => {
    const response = await appBackendApiClient.patch(
      `/admin/donation-tracking/${id}/status`,
      { status, message },
    );
    return response.data;
  },

  updateDetails: async (
    id: string,
    payload: {
      estimatedDeliveryDate?: string;
      beneficiaryRegion?: string;
      beneficiaryLabel?: string;
      beneficiaryCount?: number;
    },
  ): Promise<DonationTracking> => {
    const response = await appBackendApiClient.patch(
      `/admin/donation-tracking/${id}`,
      payload,
    );
    return response.data;
  },

  listRoutingLogs: async (params: {
    causeId?: string;
    assignedCountry?: BeneficiaryCountry;
    page?: number;
    pageSize?: number;
  } = {}): Promise<PaginatedRoutingLogs> => {
    const response = await appBackendApiClient.get(
      '/admin/donation-tracking/routing-logs',
      { params },
    );
    return response.data;
  },

  getRoutingLog: async (
    trackingId: string,
  ): Promise<DonationTrackingRoutingLog[]> => {
    const response = await appBackendApiClient.get(
      `/admin/donation-tracking/${trackingId}/routing-log`,
    );
    return response.data;
  },

  getCauseConfig: async (
    causeId: string,
  ): Promise<CauseTrackingConfig | null> => {
    const response = await appBackendApiClient.get(
      `/admin/causes/${causeId}/tracking-config`,
    );
    return response.data;
  },

  upsertCauseConfig: async (
    causeId: string,
    payload: UpsertCauseTrackingConfigPayload,
  ): Promise<CauseTrackingConfig> => {
    const response = await appBackendApiClient.put(
      `/admin/causes/${causeId}/tracking-config`,
      payload,
    );
    return response.data;
  },
};
